import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicFooter from "@/components/PublicFooter";
import BackButton from "@/components/BackButton";
import { PrinterIcon as Printer } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Shared shell for all legal / policy / informational pages.
 *
 * If `slug` is provided, fetches `/api/public/legal/{slug}` on mount.
 * When a CMS override is published for that slug, the rendered HTML
 * supersedes the JSX `children`, and the title/eyebrow/lastUpdated
 * metadata is sourced from the override. Otherwise the JSX fallback
 * is used unchanged — zero risk if the API is unreachable.
 */
export default function LegalLayout({ eyebrow, title, lastUpdated, slug, children }) {
  const [override, setOverride] = useState(null);

  useEffect(() => {
    if (!slug) { setOverride(null); return; }
    let alive = true;
    api.get(`/public/legal/${slug}`)
      .then((r) => { if (alive) setOverride(r.data); })
      .catch(() => { if (alive) setOverride(null); });
    return () => { alive = false; };
  }, [slug]);

  const effectiveDate = override?.effective_date || lastUpdated;
  const effectiveTitle = override?.label || title;

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-ink font-manrope flex flex-col print:bg-white">
      <header className="border-b border-[#E5E5E5]/60 bg-white/70 backdrop-blur-md sticky top-0 z-40 print:hidden">
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="legal-brand-link">
            <img
              src="/logo-256.png"
              alt="Armstrong K9 Registry"
              className="h-9 w-9 rounded-full object-contain"
            />
            <span className="font-spectral text-sm md:text-base tracking-tight hidden sm:inline">
              Armstrong K9 Registry
            </span>
          </Link>
          <div className="flex items-center gap-3 text-xs text-[#6B6B6B]">
            <Link to="/login" className="hover:text-ink">Sign in</Link>
            <span>·</span>
            <Link to="/register" className="hover:text-ink">Create account</Link>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-14 md:py-20">
          <div className="flex items-start justify-between gap-4 mb-2 print:hidden">
            <BackButton fallback="/" />
            <div className="flex items-center gap-2">
              {slug ? (
                <a
                  href={`/api/public/legal/${slug}/download.pdf`}
                  className="inline-flex items-center gap-1.5 text-[12px] text-white bg-ink hover:bg-crimson rounded-full px-3 py-1.5 transition-colors"
                  data-testid="legal-server-pdf-button"
                  title="Download as PDF"
                  rel="noopener"
                >
                  <Printer size={13} weight="duotone" />
                  Download PDF
                </a>
              ) : null}
              <button
                type="button"
                onClick={() => {
                  const isMac = /Mac/.test(navigator.platform);
                  toast.info(
                    `Opening the print dialog. If nothing appears, press ${isMac ? "⌘+P" : "Ctrl+P"} and choose "Save as PDF".`,
                    { duration: 6000 },
                  );
                  setTimeout(() => {
                    try {
                      window.print();
                    } catch (e) {
                      toast.error("Browser blocked the print dialog. Use ⌘+P / Ctrl+P keyboard shortcut instead.");
                    }
                  }, 150);
                }}
                className="inline-flex items-center gap-1.5 text-[12px] text-ink/65 hover:text-ink border border-stone-300 rounded-full px-3 py-1.5 transition-colors"
                data-testid="legal-print-button"
                title="Print or save as PDF (Ctrl/⌘+P)"
              >
                Print in browser
              </button>
            </div>
          </div>
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-gold mb-3">
            {eyebrow}
          </p>
          <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight" data-testid="legal-title">
            {effectiveTitle}
          </h1>
          {(effectiveDate || override?.version) && (
            <p className="text-xs text-[#6B6B6B] mt-3">
              {override?.version && (
                <>
                  Version <span className="font-mono">{override.version}</span>
                  {effectiveDate && " · "}
                </>
              )}
              {effectiveDate && (
                <>Last updated: <span className="font-mono">{effectiveDate}</span></>
              )}
            </p>
          )}
          <div
            data-testid={override ? `legal-override-${slug}` : undefined}
            className="prose prose-neutral max-w-none mt-10 text-[15px] leading-[1.75] text-ink
                       [&_h2]:font-spectral [&_h2]:text-2xl [&_h2]:tracking-tight [&_h2]:mt-10 [&_h2]:mb-3
                       [&_h3]:font-semibold [&_h3]:text-base [&_h3]:mt-6 [&_h3]:mb-2
                       [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5 [&_ul]:mb-4
                       [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-1.5 [&_ol]:mb-4
                       [&_a]:text-crimson [&_a]:underline [&_a]:underline-offset-2"
          >
            {override?.html ? (
              <div dangerouslySetInnerHTML={{ __html: override.html }} />
            ) : (
              children
            )}
          </div>
        </div>
      </main>

      <PublicFooter />
      <div className="hidden print:block px-6 pb-6 text-[10px] text-ink/50 border-t border-stone-200 pt-3 mt-6">
        Armstrong K9 Registry · armstrongk9registry.com · Generated from live customer agreement.
      </div>
    </div>
  );
}
